<template>
  <KioskLayout>

    <template #KioskLayout__bottom>
      <!-- Step 3 of 4: Portraits -->
      <KioskStepProgressIndicator
        :index="2"
        style="--ui-step-progress-number-bg: var(--Global--text-color); --ui-step-progress-number-color: var(--Global--background-color)"
      />
    </template>

    <div
      class="Portraits"
      data-testid="portraits-root"
      :class="{
        'Portraits--portrait-image-grid': useImageGrid,
        'Portraits--classic-mixed-gallery':
          classicGallery.enabled && !classicGallery.pages,
      }"
    >
      <h1 class="Portraits__title kiosk-title">
        {{ t('Portraits.title') }}
      </h1>

      <ClassicMixedPortraitGallery
        v-if="classicGallery.enabled"
        ref="gallery"
        :items="mixedIndividualItems"
        :group-items="mixedGroupItems"
        :selected-uuids="mixedSelectedUuids"
        :layout="classicGallery.layout"
        :show-preview="classicGallery.showPreview"
        :filter="classicGallery.filter"
        :pages="classicGallery.pages"
        :rail="classicGallery.rail"
        :orientation="classicGallery.orientation"
        :show-generate-more="
          portraitKiosk?.more_portraits !== false &&
          store.portraitGenerationAttemptsLeft > 0
        "
        :generate-more-disabled="generateMoreDisabled"
        :get-selected-portrait-count="getMixedSelectedPortraitCount"
        @item-click="onMixedPortraitItemClick"
        @item-load="onMixedPortraitImageLoad"
        @switch-slide="onMixedPortraitSwitchSlide"
        @toggle-selection="onMixedPortraitSelectionToggle"
        @generate-more="launchAdditionalGeneration"
        @try-again="onMixedPortraitTryAgain"
        @count-decrement="onMixedPortraitCountDecrement"
        @count-increment="onMixedPortraitCountIncrement"
      />

      <LegacyPortraitGallery
        v-else-if="!useImageGrid"
        :items="galleryItems"
        :itemsVersion="galleryVersion"
        :itemsPerSlide="PORTRAITS_PER_SLIDE"
        v-model:selectedUuids="selectedPortraitUuids"
        :showGenerateMore="
          portraitKiosk?.more_portraits !== false &&
          store.portraitGenerationAttemptsLeft > 0
        "
        :generateMoreDisabled="generateMoreDisabled"
        :getSelectedPortraitCount="getSelectedPortraitCount"
        ref="gallery"
        @item-click="onLegacyPortraitItemClick"
        @item-load="onPortraitImageLoad"
        @switch-slide="onPortraitSwitchSlide"
        @toggle-selection="onPortraitSelectionToggle"
        @generate-more="launchAdditionalGeneration"
        @try-again="onPortraitTryAgainClick"
        @count-decrement="onPortraitCountDecrement"
        @count-increment="onPortraitCountIncrement"
      />

      <PortraitImageGrid
        v-else
        :items="galleryItems"
        :layout="galleryLayout"
        :sizing="portraitGridSizing"
        :cell-width="portraitCellWidth"
        :cell-height="portraitCellHeight"
        :cell-aspect-ratio="portraitCellAspect"
        :fit="portraitFit"
        :gap="portraitGridGap"
        :cell-radius="portraitCellRadius"
        :arrow-scale="portraitArrowScale"
        :selected-uuids="selectedPortraitUuids"
        :show-generate-more="
          portraitKiosk?.more_portraits !== false &&
          store.portraitGenerationAttemptsLeft > 0
        "
        :generate-more-disabled="generateMoreDisabled"
        :getSelectedPortraitCount="getSelectedPortraitCount"
        ref="gallery"
        @item-click="onGridPortraitItemClick"
        @item-load="onPortraitImageLoad"
        @switch-slide="onPortraitSwitchSlide"
        @toggle-selection="onPortraitSelectionToggle"
        @generate-more="launchAdditionalGeneration"
        @try-again="onPortraitTryAgainClick"
        @count-decrement="onPortraitCountDecrement"
        @count-increment="onPortraitCountIncrement"
      />

      <PortraitOrderFooter
        :next-disabled="!hasAnyPortraitsSelected"
        :next-label="getNextButtonText()"
        @next="onNextButtonClick"
      />
    </div>


    <PortraitOrderDialog
      v-if="isConfirmationPopupOpen"
      variant="confirmation"
      :group-count="getGroupPortraitsCount()"
      :group-price="getGroupPortraitsPrice()"
      :individual-count="getIndividualPortraitsCount()"
      :individual-price="getIndividualPortraitsPrice()"
      :total-sum="getTotalSumFormatted()"
      @close="isConfirmationPopupOpen = false"
      @confirm="onConfirmPrintingClick"
    />

    <PortraitOrderDialog
      v-if="isPrintingLockedPopupOpen"
      variant="printing-locked"
      @close="isPrintingLockedPopupOpen = false"
    />

    <PortraitGenerationPendingDialog
      v-if="isGoPhotoGenerationPendingPopupOpen"
      :pending-count="pendingGoPhotoPortraitCount"
      :ready-count="readyGoPhotoPortraitCount"
      :send-ready-available="isSendReadyPortraitsAvailable"
      @send-ready="onSendReadyPortraitsClick"
      @wait="onGoPhotoGenerationPendingWaitClick"
    />

  </KioskLayout>
</template>


<script setup lang="ts">
import {
  computed,
  nextTick,
  onBeforeUnmount,
  onMounted,
  proxyRefs,
  reactive,
} from 'vue';
import { useI18n } from 'vue-i18n';
// biome-ignore lint/correctness/noUnusedImports: used via <KioskLayout> in <template>
import KioskLayout from '@/app/layouts/kiosk/KioskLayout.vue';
// biome-ignore lint/correctness/noUnusedImports: used via <KioskStepProgressIndicator> in <template>
import KioskStepProgressIndicator from '@/app/layouts/kiosk/KioskStepProgressIndicator.vue';
import { store } from '@/app/store';
import { useDeveloperStore } from '@/app/stores/developer';
import exposeToGlobalScope from '@/shared/utils/expose-to-global-scope';
import { warnTelemetryFailure } from '@/shared/utils/track-telemetry';
import { payment } from '../../services/Payment';
import { portraitGeneration } from '../../services/PortraitGeneration';
import { printing } from '../../services/Printing';
import { portraitAspectRatioForOrientation } from '../../shared/lib/portrait-orientation';
import {
  areHohPortraitsStillLoading,
  isHohAllPortraitsKiosk,
} from './gophoto-upload/gophotoUploadSelection';
// biome-ignore lint/correctness/noUnusedImports: used via <ClassicMixedPortraitGallery> in <template>
import ClassicMixedPortraitGallery from './portraits/ClassicMixedPortraitGallery.vue';
import {
  type GroupPortraitLike,
  includesGroupPortraits,
  individualTileRatio,
  loadedMixedItemRatio,
  type MixedPortraitGalleryItem,
} from './portraits/classic-mixed-portrait-gallery';
// biome-ignore lint/correctness/noUnusedImports: used via <LegacyPortraitGallery> in <template>
import LegacyPortraitGallery from './portraits/LegacyPortraitGallery.vue';
// biome-ignore lint/correctness/noUnusedImports: used via <PortraitGenerationPendingDialog> in <template>
import PortraitGenerationPendingDialog from './portraits/PortraitGenerationPendingDialog.vue';
// biome-ignore lint/correctness/noUnusedImports: used via <PortraitImageGrid> in <template>
import PortraitImageGrid from './portraits/PortraitImageGrid.vue';
// biome-ignore lint/correctness/noUnusedImports: used via <PortraitOrderDialog> in <template>
import PortraitOrderDialog from './portraits/PortraitOrderDialog.vue';
// biome-ignore lint/correctness/noUnusedImports: used via <PortraitOrderFooter> in <template>
import PortraitOrderFooter from './portraits/PortraitOrderFooter.vue';
import { useClassicGalleryConfig } from './portraits/useClassicGalleryConfig';
import {
  type PortraitLike,
  useParallelPortraitSlots,
} from './portraits/useParallelPortraitSlots';
import { usePortraitCounterTheme } from './portraits/usePortraitCounterTheme';
import {
  type PortraitFlow,
  usePortraitFlow,
} from './portraits/usePortraitFlow';
import { usePortraitGalleryAdapter } from './portraits/usePortraitGalleryAdapter';
import { usePortraitGridLayout } from './portraits/usePortraitGridLayout';
import { usePortraitPrintSelection } from './portraits/usePortraitPrintSelection';
import {
  readPortraitSessionContext,
  usePortraitTelemetry,
} from './portraits/usePortraitTelemetry';

interface PrintQueueSummaryItem {
  count: number;
  isGroupPortrait?: boolean;
  uuid: string;
}

interface GroupPortraitSource extends GroupPortraitLike {
  actualRatio?: number;
}

interface OrderSummary {
  groupCount: number;
  individualCount: number;
  totalSum: string;
}

interface PortraitKiosk {
  background_color?: string | null;
  button_background_color?: string | null;
  button_font_color?: string | null;
  dark_mode?: boolean;
  font_color?: string | null;
  more_portraits?: boolean;
  multi_subject_enabled?: boolean;
  parallel_generations?: number;
  portrait_orientation?: unknown;
  slug?: unknown;
}

interface PortraitsPublicApi {
  getTotalSumFormatted: () => string;
  isConfirmationPopupOpen: boolean;
  isGoPhotoGenerationPendingPopupOpen: boolean;
  isPrintingLockedPopupOpen: boolean;
  isSendReadyPortraitsAvailable: boolean;
  onConfirmPrintingClick: () => void;
  onNextButtonClick: () => void;
  readOrderSummary: () => OrderSummary;
  selectedPortraitUuids: string[];
}

const PORTRAITS_PER_SLIDE = 3;
const { t } = useI18n();
const developer = useDeveloperStore();
const portraitKiosk = computed(
  () => store.kiosk as unknown as PortraitKiosk | null
);
const portraitGrid = usePortraitGridLayout({
  kiosk: portraitKiosk,
  isVerticalKiosk: () => Boolean(store.isVerticalKiosk),
  viewportIsPortrait: () => Boolean(store.viewportIsPortrait),
  portraitsPerSlide: PORTRAITS_PER_SLIDE,
});
const {
  displayPageSize,
  // biome-ignore lint/correctness/noUnusedVariables: used in <template>
  galleryLayout,
  // biome-ignore lint/correctness/noUnusedVariables: used in <template>
  portraitArrowScale,
  portraitCellAspect,
  // biome-ignore lint/correctness/noUnusedVariables: used in <template>
  portraitCellHeight,
  // biome-ignore lint/correctness/noUnusedVariables: used in <template>
  portraitCellRadius,
  // biome-ignore lint/correctness/noUnusedVariables: used in <template>
  portraitCellWidth,
  // biome-ignore lint/correctness/noUnusedVariables: used in <template>
  portraitFit,
  // biome-ignore lint/correctness/noUnusedVariables: used in <template>
  portraitGridGap,
  // biome-ignore lint/correctness/noUnusedVariables: used in <template>
  portraitGridSizing,
  // biome-ignore lint/correctness/noUnusedVariables: used in <template>
  useImageGrid,
  portraitOrientation,
} = portraitGrid;
const kioskPortraitRatio = computed(() =>
  portraitAspectRatioForOrientation(portraitOrientation.value)
);
// biome-ignore lint/correctness/noUnusedVariables: used in <template>
const classicGallery = reactive(
  useClassicGalleryConfig({
    grid: portraitGrid,
    isVerticalKiosk: () => Boolean(store.isVerticalKiosk),
    kioskLayout: () => store.kiosk?.classic_gallery_layout,
    portraitsPerSlide: PORTRAITS_PER_SLIDE,
    viewportIsPortrait: () => Boolean(store.viewportIsPortrait),
  })
);

// A group job in progress does not hold "+": the flow then tops up the
// singles only, and starts no second group job.
// biome-ignore lint/correctness/noUnusedVariables: used in <template>
const generateMoreDisabled = computed(() => store.portraitsIsGenerating);

const useParallelDisplaySlots = computed(
  () => (portraitKiosk.value?.parallel_generations || 1) > 1
);
const {
  displaySlots,
  galleryVersion,
  getDisplaySlotRevealOrder,
  getSourcePortrait,
  portraitDisplaySyncSignature,
  settledDisplaySlotPrefixCount,
  syncDisplaySlotsFromCurrentState,
} = useParallelPortraitSlots({
  portraits: () => store.portraits,
  enabled: useParallelDisplaySlots,
  pageSize: displayPageSize,
  isVerticalKiosk: () => Boolean(store.isVerticalKiosk),
  cellAspectRatio: portraitCellAspect,
});
const galleryItems = computed(() =>
  useParallelDisplaySlots.value ? displaySlots.value : store.portraits
);
const mixedGalleryGroupPortraits = computed(() =>
  includesGroupPortraits(portraitKiosk.value?.multi_subject_enabled)
    ? (store.groupPortraits as GroupPortraitSource[])
    : []
);
const isGroupPortraitGalleryEnabled = computed(
  () =>
    classicGallery.enabled &&
    includesGroupPortraits(portraitKiosk.value?.multi_subject_enabled)
);
// Ratios measured from loaded images, by uuid: the store hands out fresh
// portrait objects, so a ratio written onto one of them is lost on the next
// read, and the seeded ratio is only a guess until the image arrives.
const measuredRatios = reactive(new Map<string, number>());
// Portrait objects are not deeply reactive; galleryVersion is the signal the
// legacy gallery re-rendered on, so the two mixed lists follow it too.
// biome-ignore lint/correctness/noUnusedVariables: used in <template>
const mixedGroupItems = computed<MixedPortraitGalleryItem[]>(() => {
  void galleryVersion.value;
  return mixedGalleryGroupPortraits.value.map((portrait) => ({
    ...portrait,
    // Before the image arrives, the ratio is the output size of the 202.
    aspectRatio:
      measuredRatios.get(portrait.uuid) ??
      portrait.actualRatio ??
      portrait.aspectRatio,
    kind: 'group' as const,
    renderKey: `group:${portrait.renderKey ?? portrait.uuid}`,
    source: portrait,
  }));
});
// biome-ignore lint/correctness/noUnusedVariables: used in <template>
const mixedIndividualItems = computed<MixedPortraitGalleryItem[]>(() => {
  void galleryVersion.value;
  return (galleryItems.value as PortraitLike[]).map((portrait) => ({
    ...portrait,
    aspectRatio: individualTileRatio(
      portrait,
      measuredRatios.get(portrait.uuid),
      kioskPortraitRatio.value
    ),
    kind: 'individual' as const,
    renderKey: `individual:${
      'renderKey' in portrait && portrait.renderKey
        ? String(portrait.renderKey)
        : portrait.uuid
    }`,
    source: portrait,
  }));
});
const mixedGalleryUuidSet = computed(
  () =>
    new Set(
      [...mixedGroupItems.value, ...mixedIndividualItems.value].map(
        (item) => item.uuid
      )
    )
);
const hohSendsAllPortraits = computed(() =>
  isHohAllPortraitsKiosk(portraitKiosk.value?.slug)
);
// biome-ignore lint/correctness/noUnusedVariables: used in <template>
const pendingGoPhotoPortraitCount = computed(() =>
  hohSendsAllPortraits.value
    ? (store.portraits as PortraitLike[]).filter(
        (portrait) => !(portrait.isPreloaded || portrait.isError)
      ).length
    : 0
);
// biome-ignore lint/correctness/noUnusedVariables: used in <template>
const readyGoPhotoPortraitCount = computed(() =>
  hohSendsAllPortraits.value
    ? (store.portraits as PortraitLike[]).filter(
        (portrait) => portrait.isPreloaded && !portrait.isError
      ).length
    : 0
);
const hasPendingGoPhotoPortraits = computed(() =>
  areHohPortraitsStillLoading(
    hohSendsAllPortraits.value,
    store.portraits as PortraitLike[]
  )
);

const {
  // biome-ignore lint/correctness/noUnusedVariables: used in <template>
  getSelectedPortraitCount,
  // biome-ignore lint/correctness/noUnusedVariables: used in <template>
  hasAnyPortraitsSelected,
  hasOnlyGroupPortraits,
  onPortraitCountDecrement,
  onPortraitCountIncrement,
  onPortraitSelectionToggle,
  restoreSelectionFromPrintingQueue,
  selectedPortraitUuids,
} = usePortraitPrintSelection({ getSourcePortrait });
const mixedSelectedUuids = computed(() =>
  Array.from(
    new Set([
      ...selectedPortraitUuids.value,
      ...(store.printingQueue as PrintQueueSummaryItem[])
        .filter((item) => mixedGalleryUuidSet.value.has(item.uuid))
        .map((item) => item.uuid),
    ])
  )
);

const individualPortraitTelemetry = usePortraitTelemetry({
  getSelectedPortraitCount: () => selectedPortraitUuids.value.length,
  getTotalPortraitCount: () => store.portraits?.length || 0,
  getPortraitContext: () =>
    readPortraitSessionContext(selectedPortraitUuids.value.length),
});
const { applyCounterTheme } = usePortraitCounterTheme({
  kiosk: portraitKiosk,
});
const portraitFlow = usePortraitFlow({
  isGroupPortraitGalleryEnabled: () => isGroupPortraitGalleryEnabled.value,
  refreshGroupPortraits: () => {
    galleryVersion.value++;
  },
  applyCounterTheme,
  getSourcePortrait,
  hasPendingGoPhotoPortraits: () => hasPendingGoPhotoPortraits.value,
  isParallelDisplaySlotsEnabled: () => useParallelDisplaySlots.value,
  isPrintLockActive: () => developer.isPrintLockActive,
  restoreSelectionFromPrintingQueue,
  syncDisplaySlotsFromCurrentState,
  telemetry: individualPortraitTelemetry,
});
const {
  isConfirmationPopupOpen,
  isGoPhotoGenerationPendingPopupOpen,
  isPrintingLockedPopupOpen,
  isSendReadyPortraitsAvailable,
  // biome-ignore lint/correctness/noUnusedVariables: used in <template>
  launchAdditionalGeneration,
  // biome-ignore lint/correctness/noUnusedVariables: used in <template>
  onGoPhotoGenerationPendingWaitClick,
  onPortraitTryAgainClick,
}: Pick<
  PortraitFlow,
  | 'isConfirmationPopupOpen'
  | 'isGoPhotoGenerationPendingPopupOpen'
  | 'isPrintingLockedPopupOpen'
  | 'isSendReadyPortraitsAvailable'
  | 'launchAdditionalGeneration'
  | 'onGoPhotoGenerationPendingWaitClick'
  | 'onPortraitTryAgainClick'
> = portraitFlow;
const { scrollToNextSlideIfNeeded } = usePortraitGalleryAdapter({
  displayPageSize,
  displaySlots,
  getDisplaySlotRevealOrder,
  getPreloadedPortraits: () => portraitGeneration.query({ preloaded: true }),
  isDisposed: portraitFlow.isDisposed,
  isParallelDisplaySlotsEnabled: useParallelDisplaySlots,
  portraitDisplaySyncSignature,
  settledDisplaySlotPrefixCount,
  syncDisplaySlotsFromCurrentState,
});

function onPortraitImageLoad(
  portrait: PortraitLike,
  _index: number,
  event: Event
): void {
  const image = event.target as HTMLImageElement | null;
  if (!(image?.naturalWidth && image.naturalHeight)) {
    return;
  }

  const actualRatio = image.naturalWidth / image.naturalHeight;
  const sourcePortrait = getSourcePortrait(portrait);

  if (sourcePortrait) {
    sourcePortrait.actualRatio = actualRatio;
    sourcePortrait.aspectRatio = actualRatio;
  }

  if (
    Object.hasOwn(portrait, 'sourcePortrait') ||
    useParallelDisplaySlots.value
  ) {
    portrait.aspectRatio = actualRatio;
  }
}

// biome-ignore lint/correctness/noUnusedVariables: used in <template>
function getMixedSelectedPortraitCount(
  item: MixedPortraitGalleryItem
): number | undefined {
  return printing.getByUuid(item.uuid)?.count;
}

// biome-ignore lint/correctness/noUnusedVariables: used in <template>
function onMixedPortraitItemClick(
  item: MixedPortraitGalleryItem,
  _index: number,
  event: MouseEvent
): void {
  individualPortraitTelemetry.trackEngagement();
  if (!item.isPreloaded || item.isError) {
    event.preventDefault();
  }
}

// biome-ignore lint/correctness/noUnusedVariables: used in <template>
function onMixedPortraitSelectionToggle(
  event: MouseEvent,
  item: MixedPortraitGalleryItem,
  isSelected: boolean
): void {
  if (item.kind === 'individual') {
    const portrait = getSourcePortrait(item.source as PortraitLike);
    if (portrait) {
      onPortraitSelectionToggle(event, portrait, isSelected);
    }
    return;
  }

  if (isSelected) {
    printing.add(item.uuid);
  } else {
    printing.remove(item.uuid);
  }
}

// biome-ignore lint/correctness/noUnusedVariables: used in <template>
function onMixedPortraitCountIncrement(
  event: MouseEvent,
  item: MixedPortraitGalleryItem
): void {
  if (item.kind === 'individual') {
    onPortraitCountIncrement(event, item.source as PortraitLike);
    return;
  }

  event.preventDefault();
  event.stopPropagation();
  printing.increment(item.uuid);
}

// biome-ignore lint/correctness/noUnusedVariables: used in <template>
function onMixedPortraitCountDecrement(
  event: MouseEvent,
  item: MixedPortraitGalleryItem
): void {
  if (item.kind === 'individual') {
    onPortraitCountDecrement(event, item.source as PortraitLike);
    return;
  }

  event.preventDefault();
  event.stopPropagation();
  printing.decrement(item.uuid);
}

// biome-ignore lint/correctness/noUnusedVariables: used in <template>
function onMixedPortraitTryAgain(item: MixedPortraitGalleryItem): void {
  if (item.kind === 'individual') {
    onPortraitTryAgainClick(item.source as PortraitLike);
    return;
  }

  void portraitGeneration
    .retryGroupPortrait(item.source as GroupPortraitSource)
    .catch((error: unknown) =>
      warnTelemetryFailure('[Portraits] Group portrait retry error:', error)
    );
}

// biome-ignore lint/correctness/noUnusedVariables: used in <template>
function onMixedPortraitImageLoad(
  item: MixedPortraitGalleryItem,
  index: number,
  event: Event
): void {
  const ratio = loadedMixedItemRatio(
    item,
    event.target as HTMLImageElement | null
  );
  if (ratio !== null) {
    measuredRatios.set(item.uuid, ratio);
    if (item.kind === 'individual') {
      onPortraitImageLoad(item.source as PortraitLike, index, event);
    }
  }
  galleryVersion.value += 1;
}

// biome-ignore lint/correctness/noUnusedVariables: used in <template>
function onMixedPortraitSwitchSlide(): void {
  individualPortraitTelemetry.trackEngagement();
}

function handlePortraitItemClick(
  portrait: PortraitLike,
  event: MouseEvent
): void {
  const sourcePortrait = getSourcePortrait(portrait);
  individualPortraitTelemetry.trackEngagement();

  if (
    !sourcePortrait ||
    sourcePortrait.isError ||
    !sourcePortrait.isPreloaded
  ) {
    event.preventDefault();
    return;
  }

  event.stopPropagation();
}

// biome-ignore lint/correctness/noUnusedVariables: used in <template>
function onLegacyPortraitItemClick(
  event: MouseEvent,
  portrait: PortraitLike
): void {
  handlePortraitItemClick(portrait, event);
}

// biome-ignore lint/correctness/noUnusedVariables: used in <template>
function onGridPortraitItemClick(
  portrait: PortraitLike,
  _index: number,
  event: MouseEvent
): void {
  handlePortraitItemClick(portrait, event);
}

// biome-ignore lint/correctness/noUnusedVariables: used in <template>
function onPortraitSwitchSlide(): void {
  individualPortraitTelemetry.trackEngagement();
}

function getPrintQueue(): PrintQueueSummaryItem[] {
  return store.printingQueue as PrintQueueSummaryItem[];
}

function getGroupQueue(): PrintQueueSummaryItem[] {
  return getPrintQueue().filter((item) => item.isGroupPortrait);
}

function getIndividualQueue(): PrintQueueSummaryItem[] {
  return getPrintQueue().filter((item) => !item.isGroupPortrait);
}

function countCopies(items: readonly PrintQueueSummaryItem[]): number {
  return items.reduce((total, item) => total + item.count, 0);
}

function getTotalSumFormatted(): string {
  return payment.getTotalSumFormatted(getPrintQueue());
}

function getGroupPortraitsCount(): number {
  return countCopies(getGroupQueue());
}

function getIndividualPortraitsCount(): number {
  return countCopies(getIndividualQueue());
}

// biome-ignore lint/correctness/noUnusedVariables: used in <template>
function getGroupPortraitsPrice(): string {
  return payment.getTotalSumFormatted(getGroupQueue());
}

// biome-ignore lint/correctness/noUnusedVariables: used in <template>
function getIndividualPortraitsPrice(): string {
  return payment.getTotalSumFormatted(getIndividualQueue());
}

function readOrderSummary(): OrderSummary {
  return {
    groupCount: getGroupPortraitsCount(),
    individualCount: getIndividualPortraitsCount(),
    totalSum: getTotalSumFormatted(),
  };
}

// biome-ignore lint/correctness/noUnusedVariables: used in <template>
function getNextButtonText(): string {
  if (hasOnlyGroupPortraits.value) {
    return (
      t('Portraits.buttonNextGroupOnly') || 'Continue with Group Portraits Only'
    );
  }

  return t('Portraits.buttonNext');
}

function onNextButtonClick(): void {
  portraitFlow.onNextButtonClick(readOrderSummary);
}

function onConfirmPrintingClick(): void {
  portraitFlow.onConfirmPrintingClick(readOrderSummary);
}

// biome-ignore lint/correctness/noUnusedVariables: used in <template>
function onSendReadyPortraitsClick(): void {
  portraitFlow.onSendReadyPortraitsClick(readOrderSummary);
}

const publicApi: PortraitsPublicApi = proxyRefs({
  isConfirmationPopupOpen,
  isGoPhotoGenerationPendingPopupOpen,
  isPrintingLockedPopupOpen,
  isSendReadyPortraitsAvailable,
  get selectedPortraitUuids(): string[] {
    return mixedSelectedUuids.value;
  },
  set selectedPortraitUuids(value: string[]) {
    selectedPortraitUuids.value = value;
  },
  getTotalSumFormatted,
  onConfirmPrintingClick,
  onNextButtonClick,
  readOrderSummary,
});

onMounted(async () => {
  exposeToGlobalScope(publicApi, 'Portraits');
  await portraitFlow.enter({
    nextTick,
    scrollToNextSlideIfNeeded,
  });
});

onBeforeUnmount(() => {
  portraitFlow.leave();
});

defineExpose(publicApi);
</script>


<style lang="scss" scoped>
.Portraits--portrait-image-grid {
  display: flex;
  flex-direction: column;
  align-items: center;
  width: var(--portrait-individual-root-width, 100%);
  height: var(--portrait-individual-root-height, auto);
  min-width: 0;
  min-height: var(--portrait-individual-root-min-height, auto);
}

.Portraits--classic-mixed-gallery {
  display: flex;
  flex-direction: column;
  align-items: center;
  /* The shell content box shrink-wraps this screen, so a percentage width
     never caps the gallery; the vertical profile defines a viewport width. */
  width: var(--portrait-individual-root-width, 100%);
  height: calc(
    var(--Global--height-landscape) - var(--kiosk-shell-gap-top) -
      var(--kiosk-shell-gap-bottom) - 1rem
  );
  min-width: 0;
  min-height: 0;
  padding: 0 2rem;
}

.Portraits--classic-mixed-gallery .Portraits__title {
  margin-top: 0;
  margin-bottom: 0.8rem;
}

.Portraits--classic-mixed-gallery
  :deep(.Portraits__footer) {
  width: min(100%, 96rem);
  padding: 0 0.2rem;
  margin-top: 0.65rem;
}

.Portraits__title {
  margin-top: var(--portrait-individual-title-margin-top, 2rem);
  margin-bottom: var(--portrait-individual-title-margin-bottom, 1rem);
}

.Portraits :deep(.ImageGallery) {
  --legacy-gallery-context-item-width: var(
    --portrait-individual-legacy-item-width,
    var(--legacy-gallery-item-width, 26rem)
  );
}
</style>
